import type { CustomListForm } from '@api'
import { Controller } from 'react-hook-form'

import { FormController } from '@/components/ui/FormController'
import { Checkbox } from '@/components/ui/checkbox'
import type { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import { Switch } from '@/components/ui/switch'
import { TextField } from '@/components/ui/text-field'

import { CustomListFormMultiSelect } from './CustomListFormMultiSelect'

export type CustomListFormField = keyof Omit<CustomListForm, 'name'>

export interface CustomListsFormFieldConfig {
  name: CustomListFormField
  label: string
  placeholder?: string
  hint?: string
  type: 'text' | 'number' | 'single-select' | 'multi-select' | 'switch'
  maxSelection?: number
  defaultValue?: string | number | string[]
  options?: SelectOptionsType
  suffix?: string
  min?: number
  viewOnly?: boolean
  entityName?: string
}

interface Props {
  field: CustomListsFormFieldConfig
  enabled: boolean
  onToggle: () => void
  viewOnly?: boolean
}

export const CustomListsFormField = ({
  field,
  enabled,
  onToggle,
  viewOnly,
}: Props) => {
  return (
    <Controller
      name={field.name}
      defaultValue={field.defaultValue}
      disabled={!enabled}
      render={({ field: formField, fieldState }) => (
        <FormController>
          <FormController.Input>
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={enabled}
                    onCheckedChange={onToggle}
                    id={field.name}
                    disabled={viewOnly}
                    testId={field.name + '-chekbox'}
                  />
                  <FormController.Label
                    className="cursor-pointer"
                    htmlFor={field.name}
                  >
                    {field.label}
                  </FormController.Label>
                </div>
                {field.hint && (
                  <FormController.Helper>{field.hint}</FormController.Helper>
                )}
              </div>

              {field.type === 'single-select' && field.options && (
                <FormSelect
                  name={field.name}
                  options={field.options}
                  className="ml-auto w-[200px] flex-shrink-0"
                  placeholder={field.placeholder || field.label}
                  disabled={!enabled || viewOnly}
                  onChange={formField.onChange}
                  triggerTestId={field.name + '-select-trigger'}
                  contentTestId={field.name + '-select-content'}
                />
              )}

              {field.type === 'multi-select' && field.options && (
                <CustomListFormMultiSelect
                  options={field.options}
                  className="ml-auto w-[200px] flex-shrink-0"
                  entityName={field.entityName || ''}
                  placeholder={field.placeholder || field.label}
                  disabled={!enabled || viewOnly}
                  value={formField.value}
                  onChange={formField.onChange}
                  triggerTestId={field.name + '-select-trigger'}
                  contentTestId={field.name + '-select-content'}
                  maxSelection={field.maxSelection}
                />
              )}

              {field.type === 'number' && (
                <TextField
                  {...formField}
                  testId={field.name + '-input'}
                  type="number"
                  suffix={field.suffix}
                  min={field.min}
                  showSuffixSeparator={!!field.suffix}
                  disabled={!enabled || viewOnly}
                  className="ml-auto w-[200px] flex-shrink-0"
                />
              )}

              {field.type === 'switch' && (
                <Switch
                  {...formField}
                  testId={field.name + '-switch'}
                  disabled={!enabled || viewOnly}
                  onCheckedChange={formField.onChange}
                  checked={formField.value}
                  className="ml-auto"
                />
              )}
            </div>
          </FormController.Input>
          {fieldState.error?.message && !viewOnly && (
            <FormController.Error>
              {fieldState.error?.message}
            </FormController.Error>
          )}
        </FormController>
      )}
    />
  )
}
